import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Globe, Languages, Menu, X, ExternalLink } from 'lucide-react';
import { translations } from '../data/translations';
import { getGenerators } from '../data/generators';

export default function Layout({ children, lang, setLang }) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLangOpen, setIsLangOpen] = useState(false);
    const location = useLocation();
    const t = (key) => translations[lang][key] || key;
    const generators = getGenerators(lang);

    const isActive = (path) => location.pathname === path;

    return (
        <div className="min-h-screen bg-[#f8fafc] text-slate-900 font-sans">
            <nav className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-[100]">
                <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
                    <Link to={`/${lang}`} className="flex items-center gap-2 cursor-pointer">
                        <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-1.5 rounded-xl text-white shadow-md shadow-blue-200">
                            <Globe size={22} />
                        </div>
                        <span className="font-black text-xl tracking-tight text-slate-800">{t('title')}</span>
                    </Link>

                    <div className="hidden md:flex items-center gap-6">
                        <Link to={`/${lang}`} className={`hover:text-blue-600 font-bold text-sm ${isActive(`/${lang}`) ? 'text-blue-600' : 'text-slate-500'}`}>{t('home')}</Link>
                        <div className="relative">
                            <button onClick={() => setIsLangOpen(!isLangOpen)} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold uppercase active:scale-95 shadow-sm">
                                <Languages size={14} className="text-blue-600" /> {translations[lang].langName}
                            </button>
                            {isLangOpen && (
                                <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-slate-100 rounded-2xl shadow-xl p-2 z-[110]">
                                    {Object.keys(translations).map(l => (
                                        <button key={l} onClick={() => { setLang(l); setIsLangOpen(false); }} className={`w-full text-left px-4 py-2.5 rounded-xl text-sm ${lang === l ? 'bg-blue-50 text-blue-600' : 'hover:bg-slate-50 text-slate-600'}`}>
                                            {translations[l].langName}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <button className="md:hidden" onClick={() => setIsMenuOpen(!isMenuOpen)}>{isMenuOpen ? <X size={28} /> : <Menu size={28} />}</button>
                </div>
            </nav>

            {isMenuOpen && (
                <div className="md:hidden fixed inset-0 top-16 bg-white z-[90] p-6 space-y-4 overflow-y-auto pb-20">
                    <Link to={`/${lang}`} onClick={() => setIsMenuOpen(false)} className="w-full block text-left font-bold p-2">{t('home')}</Link>
                    <div className="h-px bg-slate-100" />
                    {generators.map(g => (
                        <Link key={g.id} to={`/${lang}/${g.path}`} onClick={() => setIsMenuOpen(false)} className="w-full text-left flex items-center gap-4 font-bold text-slate-700 p-2 hover:bg-slate-50 rounded-lg">
                            <span className="text-blue-600">{g.icon}</span> {g.name}
                        </Link>
                    ))}
                </div>
            )}

            <main className="max-w-6xl mx-auto px-4 py-8">
                {children}
            </main>

            <footer className="bg-white border-t py-16 mt-24">
                <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-4 gap-12 text-center md:text-left">
                    <div className="col-span-1 md:col-span-2 space-y-6">
                        <span className="font-black text-2xl text-slate-800">{t('title')}</span>
                        <p className="text-slate-400 font-medium text-lg leading-relaxed">{t('footerDesc')}</p>
                    </div>
                    <div>
                        <h4 className="font-black mb-6 uppercase text-xs tracking-widest">{t('home')}</h4>
                        <ul className="text-slate-500 font-bold text-sm space-y-4">
                            <li><Link to={`/${lang}/privacy`} className="hover:text-blue-600 transition-colors">{t('privacy')}</Link></li>
                            <li><Link to={`/${lang}/terms`} className="hover:text-blue-600 transition-colors">{t('terms')}</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-black mb-6 uppercase text-xs tracking-widest">Global</h4>
                        <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                            {Object.keys(translations).map(l => (
                                <button key={l} onClick={() => setLang(l)} className={`px-2 py-1 rounded text-[10px] font-black uppercase ${lang === l ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400'}`}>{l}</button>
                            ))}
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
